import { Table } from '@tanstack/react-table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button, Icon } from '@/components';
import { useEffect } from 'react';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
}

export function DataTablePagination<TData>({ table }: DataTablePaginationProps<TData>) {
  const searchParams = new URLSearchParams(window.location.search);

  const totalPages = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1; // Convert 0-based to 1-based

  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    if (pageFromUrl) {
      const pageNumber = Number.parseInt(pageFromUrl, 10);
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        table.setPageIndex(pageNumber - 1); // Convert 1-based to 0-based
      } else {
        table.setPageIndex(0);
        updateUrlParams(1);
      }
    }
  }, [totalPages, table]);

  const updateUrlParams = (page: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    window.history.pushState({}, '', url.toString());
  };

  const updatePage = (page: number) => {
    const safePage = Math.min(Math.max(page, 1), totalPages);
    table.setPageIndex(safePage - 1); // Convert 1-based to 0-based
    updateUrlParams(safePage);
  };
  return (
    <div className="mt-6 flex items-center justify-end pb-6">
      <div className="flex items-center sm:space-x-2 w-full md:justify-end ">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value: any) => {
              table.setPageSize(Number(value));
              updatePage(1);
            }}
          >
            <SelectTrigger className="dark:focus-visible:ring-primary-100 focus-visible:border-primary-700 h-8 w-[70px] bg-white">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent
              side="top"
              className="dark:focus-visible:ring-primary-100 focus-visible:border-primary-700 bg-white"
            >
              {[10, 25, 50, 100].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {currentPage} of {totalPages}
        </div>

        <div className="flex items-end space-x-2">
          <Button
            className="bg-neutral-25 hidden enabled:hover:bg-neutral-50 lg:flex"
            onClick={() => updatePage(1)}
            disabled={!table.getCanPreviousPage()}
            color="neutral"
          >
            <span className="sr-only">Go to first page</span>
            <Icon name="chevrons-left" className="h-4 w-4" />
          </Button>
          <Button color="neutral" onClick={() => updatePage(currentPage - 1)} disabled={!table.getCanPreviousPage()}>
            <span className="sr-only">Go to previous page</span>
            <Icon name="chevron-left" className="h-4 w-4" />
          </Button>
          <Button color="neutral" onClick={() => updatePage(currentPage + 1)} disabled={!table.getCanNextPage()}>
            <span className="sr-only">Go to next page</span>
            <Icon name="chevron-left" className="h-4 w-4 rotate-180" />
          </Button>
          <Button
            className="bg-neutral-25 mr-2 hidden enabled:hover:bg-neutral-50 lg:flex"
            color="neutral"
            onClick={() => updatePage(totalPages)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <Icon name="chevrons-left" className="h-4 w-4 rotate-180" />
          </Button>
        </div>
      </div>
    </div>
  );
}
